import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BusinessRegistrationForm } from '@/types/businessRegistration';
import { motion } from 'framer-motion';
import { useGeocoding } from '@/hooks/useGeocoding';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, AlertCircle, MapPin } from 'lucide-react';

interface BusinessLocationStepProps {
  formData: BusinessRegistrationForm;
  setFormData: (data: BusinessRegistrationForm) => void;
  onNext: () => void;
  onBack: () => void;
  isLoading?: boolean;
  errors?: { [key: string]: string };
  setErrors?: (errors: { [key: string]: string }) => void;
}

export function BusinessLocationStep({ 
  formData, 
  setFormData, 
  onNext, 
  onBack,
  isLoading = false,
  errors = {},
  setErrors = () => {}
}: BusinessLocationStepProps) {
  const [isAddressValid, setIsAddressValid] = useState(false);
  const { getCoordinatesFromAddress, isLoading: isGeocodingLoading, error: geocodingError } = useGeocoding();
  
  // Combined loading state
  const isProcessing = isLoading || isGeocodingLoading;

  // Check if address has been entered
  useEffect(() => {
    const { address } = formData.businessData.location;
    const hasAddressData = address.street || address.city || address.state || address.country || address.postalCode;
    
    // If coordinates are set and not [0,0], consider address pre-validated
    if (formData.businessData.location.coordinates?.coordinates &&
        (formData.businessData.location.coordinates.coordinates[0] !== 0 || 
         formData.businessData.location.coordinates.coordinates[1] !== 0) &&
        hasAddressData) {
      setIsAddressValid(true);
    }
  }, [formData]);

  const handleAddressChange = (field: string, value: string) => {
    setIsAddressValid(false); // Reset validation when address changes
    
    // Clear specific error
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      delete newErrors.geocoding; // Clear geocoding errors too
      setErrors(newErrors);
    }
    
    setFormData({
      ...formData,
      businessData: {
        ...formData.businessData,
        location: {
          ...formData.businessData.location,
          address: {
            ...formData.businessData.location.address,
            [field]: value
          },
          coordinates: {
            type: "Point",
            coordinates: [0, 0] // Reset coordinates when address changes
          }
        }
      }
    });
  };

  const validateAddress = async () => {
    try {
      const { address } = formData.businessData.location;
      // Check if required fields are filled
      const newErrors: { [key: string]: string } = {};
      
      if (!address.street?.trim()) {
        newErrors.street = 'Street address is required';
      }
      if (!address.city?.trim()) {
        newErrors.city = 'City is required';
      }
      if (!address.state?.trim()) {
        newErrors.state = 'State/Province is required';
      }
      if (!address.country?.trim()) {
        newErrors.country = 'Country is required';
      }
      if (!address.postalCode?.trim()) {
        newErrors.postalCode = 'Postal code is required';
      }
      
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return false;
      }

      const fullAddress = `${address.street}, ${address.city}, ${address.state}, ${address.postalCode}, ${address.country}`;
      
      const geocodedResult = await getCoordinatesFromAddress(fullAddress);
      
      if (!geocodedResult) {
        setErrors({ geocoding: 'Could not find coordinates for this address. Please verify the address is correct.' });
        return false;
      }

      // Update form data with the geocoded coordinates
      setFormData({
        ...formData,
        businessData: {
          ...formData.businessData,
          location: {
            ...formData.businessData.location,
            coordinates: {
              type: "Point",
              coordinates: geocodedResult.coordinates
            },
            display_name: geocodedResult.display_name
          }
        }
      });

      setIsAddressValid(true);
      setErrors({}); // Clear all errors
      return true;
    } catch (error) {
      setErrors({ geocoding: 'Failed to validate address location. Please try again.' });
      return false;
    }
  };

  const handleValidateAndNext = async () => {
    const isValid = await validateAddress();
    if (isValid) {
      onNext();
    }
  };

  // Handle field keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent, _fieldName: string, nextFieldId?: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (nextFieldId) {
        const nextField = document.getElementById(nextFieldId);
        nextField?.focus();
      } else {
        // If no next field, try to validate
        handleValidateAndNext();
      }
    }
  };

  // Render helper for error messages
  const renderError = (fieldName: string) => {
    const error = errors[fieldName];
    if (!error) return null;
    return (
      <span className="text-sm text-red-500 mt-1">
        {error}
      </span>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="space-y-6"
    >
      <div className="space-y-4">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <MapPin className="h-5 w-5 text-primary" />
          Business Location
        </h3>
        <p className="text-sm text-gray-500">
          Enter your business's physical location. This information will be shown to visitors looking for wildlife experiences in your area.
        </p>
        
        {/* Address form */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="street">Street Address</Label>
            <Input
              id="street"
              value={formData.businessData.location.address.street}
              onChange={(e) => handleAddressChange('street', e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, 'street', 'city')}
              className={`${formData.businessData.location.address.street ? 'border-green-500 bg-green-50/30' : ''} ${errors.street ? 'border-red-500' : ''}`}
              placeholder="Enter street address"
              disabled={isProcessing}
            />
            {renderError('street')}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={formData.businessData.location.address.city}
                onChange={(e) => handleAddressChange('city', e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, 'city', 'state')}
                className={`${formData.businessData.location.address.city ? 'border-green-500 bg-green-50/30' : ''} ${errors.city ? 'border-red-500' : ''}`}
                placeholder="Enter city"
                disabled={isProcessing}
              />
              {renderError('city')}
            </div>

            <div className="space-y-2">
              <Label htmlFor="state">State/Province</Label>
              <Input
                id="state"
                value={formData.businessData.location.address.state}
                onChange={(e) => handleAddressChange('state', e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, 'state', 'country')}
                className={`${formData.businessData.location.address.state ? 'border-green-500 bg-green-50/30' : ''} ${errors.state ? 'border-red-500' : ''}`}
                placeholder="Enter state/province"
                disabled={isProcessing}
              />
              {renderError('state')}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                value={formData.businessData.location.address.country}
                onChange={(e) => handleAddressChange('country', e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, 'country', 'postalCode')}
                className={`${formData.businessData.location.address.country ? 'border-green-500 bg-green-50/30' : ''} ${errors.country ? 'border-red-500' : ''}`}
                placeholder="Enter country"
                disabled={isProcessing}
              />
              {renderError('country')}
            </div>

            <div className="space-y-2">
              <Label htmlFor="postalCode">Postal Code</Label>
              <Input
                id="postalCode"
                value={formData.businessData.location.address.postalCode}
                onChange={(e) => handleAddressChange('postalCode', e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, 'postalCode')}
                className={`${formData.businessData.location.address.postalCode ? 'border-green-500 bg-green-50/30' : ''} ${errors.postalCode ? 'border-red-500' : ''}`}
                placeholder="Enter postal code"
                disabled={isProcessing}
              />
              {renderError('postalCode')}
            </div>
          </div>
        </div>

        {/* Status messages */}
        {isProcessing && (
          <Alert className="bg-yellow-50 border-yellow-200 flex items-center">
            <Loader2 className="h-4 w-4 animate-spin text-yellow-600" />
            <AlertDescription className="text-yellow-600 ml-2">
              Validating address...
            </AlertDescription>
          </Alert>
        )}

        {(errors.geocoding || geocodingError) && (
          <Alert className="bg-red-50 border-red-200 flex items-center">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-600 ml-2">
              {errors.geocoding || geocodingError}
            </AlertDescription>
          </Alert>
        )}

        {isAddressValid && (
          <Alert className="bg-green-50 border-green-200 flex items-center">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-600 ml-2">
              Address validated successfully!
            </AlertDescription>
          </Alert>
        )}
      </div>

      <div className="flex justify-between mt-6">
        <Button 
          variant="outline" 
          onClick={onBack} 
          disabled={isProcessing}
          className="transition-all duration-200"
        >
          Back
        </Button>
        <Button 
          onClick={handleValidateAndNext}
          disabled={isProcessing}
          className="min-w-[100px] transition-all duration-200"
        >
          {isProcessing ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Validating...
            </span>
          ) : (
            'Next'
          )}
        </Button>
      </div>
    </motion.div>
  );
}